import { Component, OnInit } from '@angular/core';
import { LocalStoreService } from '@core/services';
import { AuthService } from '@core/services/auth.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { DepartmentContextService } from '@core/services/department-context.service';
import { AppROUTES, LocalStorageKeys } from './configs/app.config';
import { IUser } from './core/models/octa.model';

@Component({
  selector: 'sra-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'flagship-web';

  constructor(
    private readonly oauthService: OAuthService,
    private readonly localStore: LocalStoreService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly departmentContext: DepartmentContextService,
  ) {}

  isAuthenticated = false;
  isLogoutRoute = false;
  isUnauthorizedRoute = false;

  ngOnInit(): void {
    this.updateRouteFlags(this.router.url);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateRouteFlags(event.urlAfterRedirects || event.url);
      });

    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        this.updateRouteFlags(this.router.url);

        if (this.oauthService.hasValidAccessToken()) {
          this.isAuthenticated = true;
          this.handleAuthenticatedUser(this.isLogoutRoute);
        } else {
          this.isAuthenticated = false;
          this.handleUnauthenticatedUser(this.isLogoutRoute);
        }

        this.authService.setAuthReady(true);
      })
      .catch(() => {
        this.isAuthenticated = false;
        this.authService.setAuthReady(false);
      });

    this.listenForTokenEvents();
  }

  /* Handle authenticated user: store access token, extract user details from claims and store them, handle department context and navigation based on departments */
  private handleAuthenticatedUser(isLogoutPage: boolean): void {
    this.storeAccessToken();

    const user = this.extractUserFromClaims();
    if (!user) {
      return;
    }

    this.storeUser(user);
    this.authService.onLoginSuccess(user);

    if (!isLogoutPage) {
      this.handleDepartments();
    }
  }

  /* Handle unauthenticated user: if not on logout page, initiate login flow */
  private handleUnauthenticatedUser(isLogoutPage: boolean): void {
    if (!isLogoutPage && !this.isUnauthorizedRoute) {
      this.oauthService.initCodeFlow();
    }
  }

  private updateRouteFlags(url: string): void {
    const normalizedUrl = url.split('?')[0];
    this.isLogoutRoute = normalizedUrl === AppROUTES.LOGOUT;
    this.isUnauthorizedRoute = normalizedUrl === AppROUTES.UNAUTHORIZED;
  }

  /* Store access token in local storage */
  private storeAccessToken(): void {
    const authToken = this.oauthService.getAccessToken();
    this.localStore.store(LocalStorageKeys.AccessToken, authToken);
  }

  /* Extract user details from identity claims and return as IUser object */
  private extractUserFromClaims(): IUser | null {
    const claims = this.oauthService.getIdentityClaims() as any;
    if (!claims) {
      return null;
    }

    return {
      name: claims.name || claims.preferred_username || '',
      email: claims.preferred_username || claims.upn || '',
      sub: claims.oid || claims.sub || '',
      given_name: claims.given_name || '',
      family_name: claims.family_name || '',
      preferred_username: claims.preferred_username || claims.email || '',
    };
  }

  /* Store user details in local storage */
  private storeUser(user: IUser): void {
    this.localStore.store(LocalStorageKeys.LoggedInUserDetails, user);
  }

  /* Handle department context and navigation based on departments present in user claims */
  private handleDepartments(): void {
    const claims = this.oauthService.getIdentityClaims() as any;
    const departments: string[] | undefined = claims?.roles;

    if (!departments || departments.length === 0) {
      return;
    }

    this.departmentContext.setDepartment(departments);

    if (departments.length === 1) {
      this.departmentContext.setActiveDepartment(departments[0]);
    }

    const currentPath = this.router.url.split('?')[0];
    if (currentPath !== AppROUTES.CLIENTS || this.router.url.includes('?')) {
      this.router.navigate([AppROUTES.CLIENTS], { replaceUrl: true });
    }
  }

  /* Listen for token_received events to update access token in local storage when tokens are refreshed */
  private listenForTokenEvents(): void {
    this.oauthService.events
      .pipe(filter((e: any) => e.type === 'token_received'))
      .subscribe(() => this.storeAccessToken());
  }
}
